/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Installment schedule preview — the `lines` of POST /re/payment-plans/preview.
 * `usePlanPreview` debounces the call so typing a price does not flood it.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert } from '@mui/material';
import ReTable from './ReTable';
import ReStatusChip from './ReStatusChip';
import { Num } from './Widgets';
import {
  TRIGGER_LABELS, errorMessage, formatDate, formatMoney, formatPct, toNumber, useReApi,
} from '../api';

export const triggerText = (l) => {
  const v = l.trigger_value;
  switch (l.trigger_type) {
    case 'DAYS_AFTER_BOOKING': return `${v ?? '?'} days after booking`;
    case 'CONSTRUCTION_PCT': return `At ${v ?? '?'}% construction`;
    case 'MONTHS_AFTER_HANDOVER': return `${v ?? '?'} months after handover`;
    case 'FIXED_DATE': return l.trigger_date ? `On ${formatDate(l.trigger_date)}` : 'Fixed date';
    default: return TRIGGER_LABELS[l.trigger_type] || l.trigger_type;
  }
};

/**
 * body: {payment_plan_id?, milestones?, net_price, booking_date, project_id?}
 * Pass null to skip.
 */
export function usePlanPreview(body, delay = 400) {
  const { api, ready } = useReApi();
  const [state, setState] = useState({ lines: [], total: 0, loading: false, error: null });
  const key = body ? JSON.stringify(body) : null;

  useEffect(() => {
    if (!ready || !key) {
      setState({ lines: [], total: 0, loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    const t = setTimeout(() => {
      api.post('/re/payment-plans/preview', JSON.parse(key))
        .then((res) => {
          if (!cancelled) setState({ lines: res.data?.lines || [], total: res.data?.total ?? 0, loading: false, error: null });
        })
        .catch((err) => {
          if (!cancelled) setState({ lines: [], total: 0, loading: false, error: err });
        });
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [api, ready, key, delay]);

  return state;
}

export default function SchedulePreview({ lines, total, loading, error, currency }) {
  const sumPct = lines.reduce((a, l) => a + (toNumber(l.percentage) || 0), 0);
  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{errorMessage(error, 'Could not preview the schedule')}</Alert>}
      <ReTable
        dense
        loading={loading}
        rows={lines.map((l) => ({ ...l, id: l.sequence }))}
        emptyMessage="Choose a plan and price to preview the schedule."
        columns={[
          { key: 'sequence', label: '#', width: 36 },
          { key: 'label', label: 'Milestone', footer: 'Total' },
          { key: 'trigger', label: 'Due', render: (l) => (l.due_date ? formatDate(l.due_date) : triggerText(l)) },
          { key: 'percentage', label: '%', align: 'right', render: (l) => formatPct(l.percentage, 2), footer: formatPct(sumPct, 2) },
          {
            key: 'amount', label: 'Amount', align: 'right',
            render: (l) => <Num>{formatMoney(l.amount, currency)}</Num>,
            footer: <Num bold>{formatMoney(total, currency)}</Num>,
          },
          { key: 'status', label: 'Status', render: (l) => (l.status ? <ReStatusChip vocab="installment" status={l.status} size="xs" /> : '—') },
        ]}
      />
    </>
  );
}

SchedulePreview.propTypes = {
  lines: PropTypes.array,
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.bool,
  error: PropTypes.any,
  currency: PropTypes.string,
};
SchedulePreview.defaultProps = { lines: [], total: 0, loading: false, error: null, currency: null };
